import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, RefreshCw } from 'lucide-react';
import Navbar from '../components/Navbar';
import MobileNavbar from '../components/MobileNavbar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SecuritySummaryCards } from '../components/security/SecuritySummaryCards';
import { TimelineFilters, EMPTY_FILTERS } from '../components/security/TimelineFilters';
import { TimelineItem } from '../components/security/TimelineItem';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE = 50;

const toParams = (f, skip) => {
  const p = { limit: PAGE, skip };
  Object.entries(f).forEach(([k, v]) => { if (v && k !== 'sinceLabel') p[k] = v; });
  if (f.companyId === '__platform__') p.companyId = '';
  return p;
};

export default function SecurityTimelinePage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [options, setOptions] = useState({ companies: [], users: [], roles: [] });

  const companyNames = useMemo(() => Object.fromEntries(options.companies.map((c) => [c.id, c.name])), [options.companies]);
  const usernames = useMemo(() => Object.fromEntries(options.users.map((u) => [u.id, u.username])), [options.users]);

  const load = useCallback(async (skip = 0) => {
    setLoading(true);
    try {
      const params = toParams(filters, skip);
      const res = await axios.get(`${API}/security/timeline`, { params });
      setItems((prev) => (skip ? [...prev, ...res.data.items] : res.data.items));
      setTotal(res.data.total);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load timeline');
    } finally { setLoading(false); }
  }, [filters]);

  const loadSummary = useCallback(async () => {
    try { setSummary((await axios.get(`${API}/security/summary`)).data); } catch { /* cards are optional */ }
  }, []);

  useEffect(() => {
    loadSummary();
    axios.get(`${API}/security/filters`).then((r) => setOptions(r.data)).catch(() => {});
  }, [loadSummary]);

  useEffect(() => {
    const t = setTimeout(() => load(0), 300);
    return () => clearTimeout(t);
  }, [load]);

  const pick = (patch) => setFilters({ ...EMPTY_FILTERS, ...patch });

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <MobileNavbar />
      <div className="security-compact max-w-7xl mx-auto px-4 py-8 pb-24 md:pb-8 space-y-5" data-testid="security-timeline-page">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-emerald-600" /> Security Timeline
            </h1>
            <p className="text-sm text-slate-500 mt-1">Every login, failed attempt, deletion and change across all companies, roles and users.</p>
          </div>
          <Button size="sm" variant="outline" className="gap-2" onClick={() => { load(0); loadSummary(); }} disabled={loading} data-testid="timeline-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        <SecuritySummaryCards summary={summary} onPick={pick} />

        <Card className="p-4 space-y-3">
          <TimelineFilters filters={filters} setFilters={setFilters} options={options} />
          <div className="text-xs text-slate-500" data-testid="timeline-total">{total.toLocaleString()} events</div>
          <div data-testid="timeline-list">
            {items.map((it) => (
              <TimelineItem
                key={it.id}
                item={it}
                username={usernames[it.userId]}
                companyName={it.companyId ? (companyNames[it.companyId] || `Deleted company ${it.companyId.slice(0, 6)}`) : 'Platform'}
                onPickIp={(ip) => pick({ ip })}
                onPickUser={(user) => pick({ user })}
              />
            ))}
            {!loading && items.length === 0 && (
              <div className="py-12 text-center text-sm text-slate-500" data-testid="timeline-empty">No events match these filters.</div>
            )}
          </div>
          {items.length < total && (
            <div className="flex justify-center">
              <Button variant="outline" size="sm" onClick={() => load(items.length)} disabled={loading} data-testid="timeline-load-more">
                {loading ? 'Loading…' : `Load more (${(total - items.length).toLocaleString()} left)`}
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
